import { Asset, FolderContent, LabelSource, TaxonomyNode } from '@/api/generatedApi';
import { LabelPickerContent } from '@/components/AssetSidebar/AssetLabels/LabelPicker';
import { TitleText } from '@/components/TitleText';
import {
  invalidateAllLabelQueries,
  labelAssignedUpdater,
  labelClearedUpdater,
  labelPendingUpdater,
  optimisticallyUpdateAssetLabel,
} from '@/hooks/api/taxonomy/optimisticLabelUpdate';
import { useGetTenantTaxonomy } from '@/hooks/api/taxonomy/useGetTenantTaxonomy';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useToast } from '@/hooks/useToast';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetsFromFolderContents } from '@/utils/assets';
import { pluralize } from '@/utils/format';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

type LabelAction = 'assign' | 'remove' | 'auto';
type LabelMode = 'manual' | 'auto';

const pickUpdater = (
  action: LabelAction,
  asset: Asset,
  selectedNode: TaxonomyNode | undefined,
  selectedDepth: number
) => {
  if (action === 'remove') return labelClearedUpdater;
  if (action === 'auto') return labelPendingUpdater;
  if (!selectedNode) throw new Error('selectedNode required for assign');
  return labelAssignedUpdater({
    id: asset.label?.summary?.id ?? selectedNode.id,
    taxonomyNodeId: selectedNode.id,
    name: selectedNode.name,
    depth: selectedDepth,
    source: LabelSource.User,
  });
};

const applyToAsset = async (
  asset: Asset,
  action: LabelAction,
  taxonomyId: string,
  tenantId: string,
  selectedNode: TaxonomyNode | undefined,
  selectedDepth: number,
  queryClient: QueryClient
) => {
  if (action === 'remove') await api.taxonomy.unassignLabel(tenantId, asset.id, taxonomyId);
  else if (action === 'auto') await api.taxonomy.autoLabelAsset(tenantId, asset.id);
  else await api.taxonomy.assignLabel(tenantId, asset.id, taxonomyId, { taxonomyNodeId: selectedNode!.id });
  optimisticallyUpdateAssetLabel({
    queryClient,
    assetId: asset.id,
    tenantId,
    updater: pickUpdater(action, asset, selectedNode, selectedDepth),
    fallbackAsset: asset,
  });
};

const successText = (action: LabelAction, count: number) => {
  const noun = pluralize(count, 'asset', 'assets');
  if (action === 'remove') return `Removed labels from ${noun}`;
  if (action === 'auto') return `Auto-label started for ${noun}`;
  return `Labeled ${noun}`;
};

const errorText = (action: LabelAction, count: number) =>
  `Failed to ${action === 'auto' ? 'start auto-label' : 'update'} for ${pluralize(count, 'asset', 'assets')}`;

const autoModeText = (isSingle: boolean, eligibleCount: number, skippedCount: number) => {
  if (isSingle) return 'Physna AI will pick the best matching label for this asset.';
  const head =
    eligibleCount > 0
      ? `Physna AI will label ${pluralize(eligibleCount, 'asset', 'assets')}.`
      : 'No eligible assets — every selected asset already has a user-assigned label.';
  const tail =
    skippedCount > 0 ? ` ${pluralize(skippedCount, 'asset', 'assets')} will be skipped (user-assigned label).` : '';
  return head + tail;
};

export const BulkLabelModal = ({
  open,
  onClose,
  selectedContent,
  onLabelComplete,
}: {
  open: boolean;
  onClose: () => void;
  selectedContent: FolderContent[];
  onLabelComplete?: () => void;
}) => {
  const { getTenantId } = useTenants();
  const { taxonomy } = useGetTenantTaxonomy();
  const { isTaxonomyAiClassificationEnabled } = useFeatureFlags();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const assets = getAssetsFromFolderContents(selectedContent);
  const isSingle = assets.length === 1;
  const singleSummary = isSingle ? assets[0]?.label?.summary : undefined;
  const labeledCount = assets.filter(a => a.label?.summary).length;
  const autoEligible = assets.filter(a => a.label?.summary?.source !== LabelSource.User);
  const autoSkipped = assets.length - autoEligible.length;
  const showAutoOption =
    isTaxonomyAiClassificationEnabled && (isSingle ? singleSummary?.source !== LabelSource.User : true);

  const [mode, setMode] = useState<LabelMode>('manual');
  const [selectedNode, setSelectedNode] = useState<TaxonomyNode | undefined>();
  const [selectedDepth, setSelectedDepth] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const abortReference = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setMode('manual');
      setSelectedNode(undefined);
      setSelectedDepth(0);
    }
    return () => abortReference.current?.abort();
  }, [open]);

  const handleClose = () => {
    abortReference.current?.abort();
    setIsProcessing(false);
    setCompletedCount(0);
    setTotalCount(0);
    onClose();
  };

  const targetsFor = (action: LabelAction) => {
    if (action === 'remove') return assets.filter(a => a.label?.summary);
    if (action === 'auto') return autoEligible;
    return assets;
  };

  const runOperation = async (action: LabelAction) => {
    if (!taxonomy) return;
    const targets = targetsFor(action);
    setIsProcessing(true);
    setCompletedCount(0);
    setTotalCount(targets.length);

    const abortController = new AbortController();
    abortReference.current = abortController;
    const tenantId = getTenantId();
    let successCount = 0;
    let errorCount = 0;

    for (const asset of targets) {
      if (abortController.signal.aborted) break;
      try {
        await applyToAsset(asset, action, taxonomy.id, tenantId, selectedNode, selectedDepth, queryClient);
        if (!abortController.signal.aborted) successCount += 1;
      } catch {
        if (!abortController.signal.aborted) errorCount += 1;
      }
      if (!abortController.signal.aborted) setCompletedCount(p => p + 1);
    }

    setIsProcessing(false);
    abortReference.current = undefined;
    if (successCount > 0) invalidateAllLabelQueries(queryClient);

    if (abortController.signal.aborted) {
      showToast({
        message: `Operation cancelled. ${pluralize(successCount, 'asset', 'assets')} updated.`,
        type: 'info',
      });
      return;
    }
    if (successCount > 0) showToast({ message: successText(action, successCount), type: 'success' });
    if (errorCount > 0) showToast({ message: errorText(action, errorCount), type: 'error' });
    onLabelComplete?.();
    handleClose();
  };

  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const canAssign = mode === 'manual' && !isProcessing && !!selectedNode;
  const canRunAuto = mode === 'auto' && !isProcessing && (isSingle || autoEligible.length > 0);
  const showRemove =
    mode === 'manual' && !isProcessing && !selectedNode && (isSingle ? !!singleSummary : labeledCount > 0);
  const currentNodeId = selectedNode?.id ?? (isSingle ? singleSummary?.taxonomyNodeId : undefined);

  return (
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : handleClose}
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: '12px' } } }}
    >
      <TitleText sx={{ padding: '24px 8px 0 24px' }}>{isSingle ? 'Label asset' : 'Manage labels'}</TitleText>
      <DialogContent sx={{ padding: '8px 24px 24px 24px' }}>
        {!isSingle && (
          <Typography variant='body2' color='textSecondary' gutterBottom>
            {pluralize(assets.length, 'asset', 'assets')} selected
          </Typography>
        )}
        {!isProcessing && showAutoOption && (
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, next: LabelMode | null) => next && setMode(next)}
            size='small'
            sx={{ my: 1 }}
          >
            <ToggleButton value='manual' sx={{ textTransform: 'none' }}>
              Pick a label
            </ToggleButton>
            <ToggleButton value='auto' sx={{ textTransform: 'none' }}>
              <AutoAwesomeIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Auto-label
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        {taxonomy && !isProcessing && mode === 'manual' && (
          <LabelPickerContent
            taxonomyId={taxonomy.id}
            currentNodeId={currentNodeId}
            onSelect={(node, depth) => {
              setSelectedNode(node);
              setSelectedDepth(depth);
            }}
            onRemove={selectedNode ? () => setSelectedNode(undefined) : undefined}
          />
        )}
        {!isProcessing && mode === 'auto' && (
          <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
            {autoModeText(isSingle, autoEligible.length, autoSkipped)}
          </Typography>
        )}
        {isProcessing && !isSingle && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant='body2' color='textSecondary'>
                {completedCount} of {totalCount} assets
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress variant='determinate' value={progress} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
        {showRemove && (
          <Button
            onClick={() => void runOperation('remove')}
            color='error'
            sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px', mr: 'auto' }}
          >
            {isSingle ? 'Remove label' : 'Remove labels'}
          </Button>
        )}
        <Button
          onClick={handleClose}
          disabled={isProcessing}
          sx={{ textTransform: 'none', borderRadius: '12px', padding: '12px 16px' }}
        >
          {isProcessing ? 'Processing...' : 'Cancel'}
        </Button>
        {mode === 'manual' && (
          <Button
            onClick={() => void runOperation('assign')}
            color='primary'
            variant='contained'
            disabled={!canAssign}
            sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px' }}
          >
            Assign label
          </Button>
        )}
        {mode === 'auto' && (
          <Button
            onClick={() => void runOperation('auto')}
            color='primary'
            variant='contained'
            disabled={!canRunAuto}
            sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px' }}
          >
            Auto-label
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
